import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  PersonOutlined,
  CheckCircleOutlined,
  ArrowForwardOutlined,
  UploadFileOutlined,
  WorkOutlined,
  SchoolOutlined,
  DescriptionOutlined,
  StarOutlined,
  FitnessCenterOutlined,
  CardMembershipOutlined,
  TuneOutlined
} from '@mui/icons-material';
import DashboardCard, { CardHeader } from '../DashboardCard/DashboardCard';

function RingProgress({ pct = 0 }) {
  const size = 80;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;

  let color = '#0094B3';
  if (pct < 30) {
    color = '#EF4444';
  } else if (pct < 70) {
    color = '#F59E0B';
  }

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8ED" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.4,0,0.2,1), stroke 0.5s ease' }}
      />
    </svg>
  );
}

function useSections(profile, completion) {
  return useMemo(
    () => [
      {
        key: 'cv',
        label: 'Upload CV',
        icon: <UploadFileOutlined />,
        done: (completion?.UploadCV ?? 0) > 0,
        weight: completion?.UploadCV ?? 30,
        path: '/my-profile'
      },
      {
        key: 'personal',
        label: 'Personal details',
        icon: <PersonOutlined />,
        done: (completion?.PersonalDetails ?? 0) > 0,
        weight: completion?.PersonalDetails ?? 10,
        path: '/my-profile'
      },
      {
        key: 'preferences',
        label: 'Job preferences',
        icon: <TuneOutlined />,
        done: (completion?.JobPreference ?? 0) > 0,
        weight: completion?.JobPreference ?? 10,
        path: '/my-profile'
      },
      {
        key: 'summary',
        label: 'About / summary',
        icon: <DescriptionOutlined />,
        done: (completion?.Summary ?? 0) > 0,
        weight: completion?.Summary ?? 5,
        path: '/my-profile'
      },
      {
        key: 'work',
        label: 'Work experience',
        icon: <WorkOutlined />,
        done: (completion?.WorkExperiences ?? 0) > 0,
        weight: completion?.WorkExperiences ?? 10,
        path: '/my-profile'
      },
      {
        key: 'education',
        label: 'Education',
        icon: <SchoolOutlined />,
        done: (completion?.Educations ?? 0) > 0,
        weight: completion?.Educations ?? 10,
        path: '/my-profile'
      },
      {
        key: 'certs',
        label: 'Certifications',
        icon: <CardMembershipOutlined />,
        done: (completion?.Certificates ?? 0) > 0,
        weight: completion?.Certificates ?? 10,
        path: '/my-profile'
      },
      {
        key: 'skills',
        label: 'Skills',
        icon: <FitnessCenterOutlined />,
        done: (completion?.Skills ?? 0) > 0,
        weight: completion?.Skills ?? 5,
        path: '/my-profile'
      },
      {
        key: 'apply',
        label: 'Apply to a job',
        icon: <StarOutlined />,
        done: (completion?.IsJobApplied ?? 0) > 0,
        weight: completion?.IsJobApplied ?? 10,
        path: '/dashboard/jobs'
      }
    ],
    [completion, profile]
  );
}

export default function MyProfileCard({ animDelay = 0 }) {
  const navigate = useNavigate();

  const profile = useSelector((s) => s.user?.profile);
  const completion = useSelector((s) => s.user?.profileCompletion);

  const pct = completion?.ProfileCompletionPercentage ?? 0;
  const isComplete = pct >= 100;

  const sections = useSections(profile, completion);
  const incomplete = sections.filter((s) => !s.done);
  const done = sections.filter((s) => s.done);

  const nextStep = incomplete.sort((a, b) => b.weight - a.weight)[0];

  const remaining = incomplete.filter((s) => s.key !== nextStep?.key);

  return (
    <DashboardCard animDelay={animDelay}>
      <CardHeader icon={<PersonOutlined />} title="My Profile" />

      <div className="px-5 pb-5 flex flex-col gap-4">
        {/* ── Ring + headline ── */}
        <div className="flex items-center gap-4">
          <div className="relative shrink-0 w-[80px] h-[80px]">
            <RingProgress pct={pct} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[16px] font-bold text-[#022D41] leading-none">{pct}%</span>
              <span className="text-[10px] text-[#6B7A85] mt-0.5">complete</span>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            {isComplete ? (
              <>
                <div className="text-[15px] font-semibold text-[#022D41]">Profile complete</div>
                <div className="text-[13px] text-[#6B7A85] mt-1 leading-snug">Visible to recruiters worldwide.</div>
              </>
            ) : (
              <>
                <div className="text-[15px] font-semibold text-[#022D41]">
                  {incomplete.length} section{incomplete.length !== 1 ? 's' : ''} to go
                </div>
                <div className="text-[13px] text-[#6B7A85] mt-1 leading-snug">
                  Complete your profile to appear in recruiter searches.
                </div>
              </>
            )}
          </div>
        </div>

        {nextStep && (
          <button
            onClick={() => navigate(nextStep.path)}
            className={[
              'flex items-center gap-3 w-full px-4 py-3',
              'bg-[#E6F4F8] border border-[#BAE3EE] rounded-[10px]',
              'cursor-pointer hover:bg-[#D5EEF6] transition-colors duration-150 text-left',
              'group'
            ].join(' ')}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-[8px] bg-[#0094B3]/10 shrink-0 text-[#0094B3]">
              {React.cloneElement(nextStep.icon, { sx: { fontSize: 17 } })}
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#022D41] leading-tight">Next: {nextStep.label}</div>
              <div className="text-[12px] text-[#0094B3] mt-0.5 font-medium">+{nextStep.weight}% to your profile</div>
            </div>

            <ArrowForwardOutlined
              sx={{ fontSize: 16, color: '#0094B3', flexShrink: 0 }}
              className="group-hover:translate-x-[2px] transition-transform duration-150"
            />
          </button>
        )}

        {remaining.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="text-[11px] font-semibold text-[#9AA3AE] uppercase tracking-wider mb-0.5">
              Also incomplete
            </div>
            <div className="flex flex-wrap gap-1.5">
              {remaining.map((s) => (
                <button
                  key={s.key}
                  onClick={() => navigate(s.path)}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-[#F0F4F7] border border-[#E2E8ED] rounded-full text-[12px] text-[#6B7A85] font-medium cursor-pointer hover:bg-[#E2E8ED] hover:text-[#022D41] transition-colors duration-150 border-none"
                >
                  {React.cloneElement(s.icon, { sx: { fontSize: 12 } })}
                  {s.label}
                  <span className="text-[11px] text-[#0094B3] font-semibold">+{s.weight}%</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {done.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="text-[11px] font-semibold text-[#9AA3AE] uppercase tracking-wider mb-0.5">Completed</div>
            <div className="flex flex-col gap-1">
              {done.map((s) => (
                <div key={s.key} className="flex items-center gap-2 text-[13px] text-[#6B7A85]">
                  <CheckCircleOutlined sx={{ fontSize: 14, color: '#10B981', flexShrink: 0 }} />
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => navigate('/my-profile')}
          className="flex items-center justify-between w-full text-[14px] text-[#0094B3] font-semibold bg-transparent border border-[#E2E8ED] rounded-[8px] px-4 py-2.5 cursor-pointer hover:bg-[#F0F4F7] transition-colors duration-150 mt-1"
        >
          View full profile
          <ArrowForwardOutlined sx={{ fontSize: 15 }} />
        </button>
      </div>
    </DashboardCard>
  );
}
